import { ChevronDown, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './searchable-select.module.css';

export interface SearchableSelectOption {
  id: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  isLoading?: boolean;
  loadingText?: string;
  emptyText?: string;
  disabled?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search...',
  isLoading = false,
  loadingText = 'Loading...',
  emptyText = 'No options found',
  disabled = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(
    () => options.find(opt => opt.id === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(opt => opt.label?.toLowerCase().includes(query));
  }, [options, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const isDisabled = disabled || isLoading;
  const triggerClass = isDisabled
    ? `${styles.trigger} ${styles.disabled}`
    : styles.trigger;
  const labelClass = selectedOption ? styles.hasValue : undefined;

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={triggerClass}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}>
        <span className={labelClass}>
          {isLoading ? loadingText : selectedOption?.label || placeholder}
        </span>
        <aside>
          {value && !isDisabled && (
            <button type="button" onClick={handleClear}>
              <X />
            </button>
          )}
          <ChevronDown />
        </aside>
      </button>

      {isOpen && (
        <nav className={styles.dropdown}>
          <search>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              autoFocus
              onClick={e => e.stopPropagation()}
            />
          </search>
          {filteredOptions.length === 0 ? (
            <p className={styles.emptyState}>{emptyText}</p>
          ) : (
            <ul>
              {filteredOptions.map(option => (
                <li
                  key={option.id}
                  className={option.id === value ? styles.selected : undefined}
                  onClick={() => handleSelect(option.id)}>
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </nav>
      )}
    </div>
  );
};
